import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, TextField, Typography,
} from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { EmailTemplateService } from '../../services/emailTemplate.service';
import { AuthService } from '../../services/auth.service';
import {
  EMAIL_BODY_MAX, EMAIL_SUBJECT_MAX, EmailTemplate, EmailTemplateKey, RenderedEmail, missingRequiredField,
  unknownPlaceholders,
} from '../../interfaces/sales/emailTemplate.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

type Target = 'subject' | 'body';

/**
 * F7a: Sales settings > E-mail templates. Edit the subject and body of each customer mail, insert merge fields,
 * preview with sample data and reset to the built-in text. Nothing on this page sends an e-mail.
 */
const EmailTemplatesSettings: React.FC = () => {
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.EMAIL_TEMPLATE_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [key, setKey] = useState<EmailTemplateKey | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState<RenderedEmail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastTarget = useRef<Target>('body');

  const current = templates.find(t => t.key === key) ?? null;

  const select = (t: EmailTemplate | null) => {
    setKey(t ? t.key : null);
    setSubject(t ? t.subject : '');
    setBody(t ? t.body : '');
    setPreview(null);
  };

  useEffect(() => {
    (async () => {
      try {
        const list = await EmailTemplateService.list(institutionId);
        setTemplates(list);
        select(list[0] ?? null);
      } catch (e) {
        toast.showApiError(e, 'Failed to load e-mail templates');
      } finally {
        setLoading(false);
      }
    })();
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const replaceOne = (saved: EmailTemplate) => {
    setTemplates(prev => prev.map(t => (t.key === saved.key ? saved : t)));
    select(saved);
  };

  /** Inserts {{field}} at the caret of the last focused box (the body by default). */
  const insertField = (field: string) => {
    const token = `{{${field}}}`;
    const target = lastTarget.current;
    const el = target === 'subject' ? subjectRef.current : bodyRef.current;
    const value = target === 'subject' ? subject : body;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    if (target === 'subject') setSubject(next); else setBody(next);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      }
    });
  };

  const unknown = current ? unknownPlaceholders(`${subject}\n${body}`, current.fields) : [];
  const missing = current ? missingRequiredField(current.key, subject, body) : null;
  const dirty = !!current && (subject !== current.subject || body !== current.body);
  const invalid = !subject.trim() || !body.trim() || subject.length > EMAIL_SUBJECT_MAX
    || body.length > EMAIL_BODY_MAX || unknown.length > 0 || !!missing;

  const runPreview = async () => {
    if (!key) return;
    try {
      setBusy(true);
      setPreview(await EmailTemplateService.preview(institutionId, key, { subject, body }));
    } catch (e) {
      toast.showApiError(e, 'Failed to preview the template');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!key) return;
    try {
      setBusy(true);
      replaceOne(await EmailTemplateService.save(institutionId, key, { subject: subject.trim(), body }));
      toast.showSuccess('Template saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save the template');
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!key) return;
    try {
      setBusy(true);
      replaceOne(await EmailTemplateService.reset(institutionId, key));
      toast.showSuccess('Template reset to the built-in text');
    } catch (e) {
      toast.showApiError(e, 'Failed to reset the template');
    } finally {
      setBusy(false);
      setConfirmReset(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100 }} data-help-page="sales.emailTemplates.page">
      <Typography variant="h5" sx={{ mb: 1 }}>E-mail Templates</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The text of the e-mails sent to customers and suppliers. Without a custom template the built-in text is used.
        Mails are still only sent when someone clicks E-mail on a document; previews are never sent.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <Box sx={{ flex: '1 1 280px', maxWidth: 420 }}>
          <SearchableSelect<EmailTemplate>
            data-help="sales.emailTemplates.template"
            label="Template"
            size="small"
            options={templates}
            value={key}
            getOptionId={t => t.key}
            getOptionLabel={t => (t.customised ? `${t.label} (customised)` : t.label)}
            onChange={(_id, t) => select(t)}
            clearable={false}
            fullWidth
          />
        </Box>
        {current && (
          <Chip size="small" color={current.customised ? 'primary' : 'default'}
            label={current.customised ? 'Customised' : 'Built-in text'} />
        )}
      </Box>

      {current && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
          <Paper sx={{ p: 2, flex: '1 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="sales.emailTemplates.section.editor">
            <TextField
              data-help="sales.emailTemplates.subject"
              label="Subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              onFocus={() => { lastTarget.current = 'subject'; }}
              inputRef={subjectRef}
              inputProps={{ maxLength: EMAIL_SUBJECT_MAX }}
              helperText={`${subject.length}/${EMAIL_SUBJECT_MAX}`}
              disabled={!canManage}
              fullWidth
            />
            <TextField
              data-help="sales.emailTemplates.body"
              label="Body"
              value={body}
              onChange={e => setBody(e.target.value)}
              onFocus={() => { lastTarget.current = 'body'; }}
              inputRef={bodyRef}
              multiline
              minRows={10}
              inputProps={{ maxLength: EMAIL_BODY_MAX }}
              helperText={`Plain text; line breaks are kept. ${body.length}/${EMAIL_BODY_MAX}`}
              disabled={!canManage}
              fullWidth
            />
            {unknown.length > 0 && (
              <Alert severity="error">
                Unknown field{unknown.length > 1 ? 's' : ''}: {unknown.map(u => `{{${u}}}`).join(', ')}. Use the fields below.
              </Alert>
            )}
            {missing && (
              <Alert severity="error">
                This mail must include {`{{${missing}}}`} so the customer receives the payment link.
              </Alert>
            )}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Merge fields{canManage ? ' (click to insert at the cursor)' : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {current.fields.map(f => (
                  <Chip key={f} size="small" variant="outlined" label={`{{${f}}}`}
                    onClick={canManage ? () => insertField(f) : undefined} />
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
              <Button data-help="sales.emailTemplates.preview" onClick={runPreview} disabled={busy || invalid}>Preview</Button>
              {canManage && (
                <>
                  <Button data-help="sales.emailTemplates.reset" color="warning" disabled={busy || !current.customised} onClick={() => setConfirmReset(true)}>
                    Reset to default
                  </Button>
                  <Button data-help="sales.emailTemplates.save" variant="contained" disabled={busy || invalid || (!dirty && current.customised)} onClick={save}>
                    Save
                  </Button>
                </>
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 2, flex: '1 1 360px', minWidth: 0 }} data-testid="email-preview" data-help-section="sales.emailTemplates.section.preview">
            <Typography variant="subtitle1">Preview</Typography>
            <Typography variant="caption" color="text.secondary">Sample data. Not sent.</Typography>
            <Divider sx={{ my: 1 }} />
            {preview ? (
              <>
                <Typography variant="body2" color="text.secondary">Subject</Typography>
                <Typography sx={{ mb: 2, fontWeight: 600, overflowWrap: 'anywhere' }}>{preview.subject}</Typography>
                <Typography variant="body2" color="text.secondary">Body</Typography>
                {/* Rendered as text, never as HTML: the mail's HTML part is the same text, escaped server-side. */}
                <Typography component="div" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {preview.plainBody}
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary">Click Preview to see the mail with sample values.</Typography>
            )}
          </Paper>
        </Box>
      )}

      <ConfirmDialog
        open={confirmReset}
        title="Reset to default"
        message="The custom text is removed and the built-in text is used again."
        confirmLabel="Reset"
        confirmColor="warning"
        submitting={busy}
        onCancel={() => setConfirmReset(false)}
        onConfirm={reset}
      />
    </Box>
  );
};

export default EmailTemplatesSettings;
